import React, { useState, useCallback, useEffect } from 'react';
import useGameStore from '../store/gameStore';
import useAudioStore from '../store/audioStore';
import { useTranslation } from '../i18n';
import { isWorkingDay } from '../utils/format';
import { InteractiveMap } from '../components/game/InteractiveMap';
import { Leaderboard } from '../components/game/Leaderboard';
import { ContractAssignModal } from '../components/game/ContractAssignModal';
import ContractArgumentationModal from '../components/game/ContractArgumentationModal';
import { CancelContractModal } from '../components/game/CancelContractModal';
import { Activity } from 'lucide-react';
import canExecuteNow from '../utils/canExecuteNow';

import { KpiStrip } from '../components/dashboard/KpiStrip';
import { DayControls } from '../components/dashboard/DayControls';
import { TradeshowBanner } from '../components/dashboard/TradeshowBanner';
import { AvailableContracts } from '../components/dashboard/AvailableContracts';
import { CorkBoardContracts } from '../components/dashboard/CorkBoardContracts';
import { ContractDetailModal } from '../components/dashboard/ContractDetailModal';
import { ActiveContractsList } from '../components/dashboard/ActiveContractsList';
import { MonthlyKpis } from '../components/dashboard/MonthlyKpis';
import { AlertsList } from '../components/dashboard/AlertsList';
import { TreasuryChart } from '../components/dashboard/TreasuryChart';
import FuelEvolutionInsight from '../components/dashboard/FuelEvolutionInsight';
import MarketBoomWidget from '../components/dashboard/MarketBoomWidget';
import ObjectivesWidget from '../components/dashboard/ObjectivesWidget';
import TenderPipelineWidget from '../components/dashboard/TenderPipelineWidget';
import CommercialOpsWidget from '../components/dashboard/CommercialOpsWidget';
import MarketingSalonWidget from '../components/dashboard/MarketingSalonWidget';
import FleetOverviewWidget from '../components/dashboard/FleetOverviewWidget';
import BenefitsCard from '../components/dashboard/BenefitsCard';
import UrgentMessagesBanner from '../components/dashboard/UrgentMessagesBanner';
import { toast } from '../components/ui/sonner';
import { resolveClient } from '../utils/resolveClient';
import { argumentationOutcome, getClientDriversForContract } from '../utils/argumentation';
import { EMPTY_OBJECT } from '../store/stableEmpties';

export default function Dashboard() {
  const {
    company, day, month, year, totalDays, treasury, reputation, notoriety, phase,
    vehicles, drivers, activeContracts, availableContracts, activeInvestments, hiredSupport,
    pendingEvents, lastDayRevenue, lastDayExpenses, monthlyRevenues, monthlyExpenses,
    treasuryHistory, alerts, ownedDepots,
    nextDay, hasSupportUnlock, attendTradeShow, tradeShowMonth, hasMarketIntelAccess,
  } = useGameStore();

  const playSound = useAudioStore((s) => s.play);
  const { t } = useTranslation();
  // 11/06/2026 (soir 9) — per-client argumentation memory + setters.
  const clientArgumentationDefaults = useGameStore(s => s.clientArgumentationDefaults || EMPTY_OBJECT);
  const rememberClientArgumentationChoice = useGameStore(s => s.rememberClientArgumentationChoice);
  const resetClientArgumentationChoice = useGameStore(s => s.resetClientArgumentationChoice);

  const [selectedContract, setSelectedContract] = useState(null);
  const [cancellingContract, setCancellingContract] = useState(null);
  const [fastForwarding, setFastForwarding] = useState(false);
  // 03/06/2026 Phase 3 bis — argumentation mini-game state.
  const [argumentationContract, setArgumentationContract] = useState(null);
  // 03/06/2026 Phase 3 — toggle between dense table and cork board.
  // Persisted in localStorage to survive reloads.
  const [contractView, setContractView] = useState(() => {
    try { return localStorage.getItem('hst-contract-view') || 'table'; } catch { return 'table'; }
  });
  const [openContractDetail, setOpenContractDetail] = useState(null);
  const handleContractViewChange = (view) => {
    setContractView(view);
    try { localStorage.setItem('hst-contract-view', view); } catch { /* ignore */ }
  };

  const working = isWorkingDay(day);
  // 08/02/2026 — Bug fix critique production : `pendingEvents.length` comptait
  // TOUS les events, y compris ceux avec `scheduledFor` dans le futur (suite
  // d'audit anti-fraude, audit RGPD, follow-ups +60j…). Conséquence : le
  // bouton « Passer un jour » restait grisé éternellement avec "Résoudre
  // les événements", alors qu'EventPopup ne montrait rien (il filtre
  // déjà les events futurs). On n'utilise désormais que les events
  // RÉSOLVABLES (scheduledFor absent ou échu) pour décider du blocage.
  const hasPending = pendingEvents.some(e => !e.scheduledFor || e.scheduledFor <= totalDays);
  const dailyDelta = lastDayRevenue - lastDayExpenses;

  const handleNextDay = () => {
    playSound('nextDay');
    nextDay();
  };

  const handleFastForward = useCallback(async (numDays) => {
    playSound('fastForward');
    setFastForwarding(true);
    for (let i = 0; i < numDays; i++) {
      const state = useGameStore.getState();
      // 08/02/2026 — Bug fix prod : même filtre que hasPending — un
      // event « scheduledFor » futur ne doit pas casser le fast-forward.
      const tDays = state.totalDays || 0;
      const hasResolvable = (state.pendingEvents || []).some(
        e => !e.scheduledFor || e.scheduledFor <= tDays
      );
      if (hasResolvable) break;
      const success = state.nextDay();
      if (!success) break;
    }
    setFastForwarding(false);
  }, [playSound, setFastForwarding]);

  // 08/02/2026 — Q2 user prod : raccourci clavier BARRE ESPACE = passer
  // un jour. Désactivé si :
  //   • un champ texte/textarea/select est focusé (saisie en cours),
  //   • une modale est ouverte (pendingEvents résolvables → blocage),
  //   • un input contentEditable est focusé.
  // On utilise `keydown` (plus rapide que `keyup`) avec preventDefault
  // pour empêcher le scroll natif déclenché par Space sur <body>.
  useEffect(() => {
    function onKey(e) {
      if (e.code !== 'Space' && e.key !== ' ') return;
      // Skip if a form element is focused
      const target = e.target;
      if (!target) return;
      const tag = (target.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || target.isContentEditable) return;
      // Skip if any modal/dialog is open (role="dialog" or known z-index overlays)
      if (document.querySelector('[role="dialog"], .ReactModal__Overlay--after-open')) return;
      // Skip if an unresolved event is present
      const state = useGameStore.getState();
      const tDays = state.totalDays || 0;
      const blocked = (state.pendingEvents || []).some(ev => !ev.scheduledFor || ev.scheduledFor <= tDays);
      if (blocked) return;
      // OK — advance one day
      e.preventDefault();
      playSound('nextDay');
      state.nextDay();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [playSound]);

  const chartData = treasuryHistory.slice(-30).map((h, i) => ({
    name: `J${i + 1}`,
    trésorerie: Math.round(h.treasury),
    revenus: h.revenue,
    charges: h.expenses,
  }));

  // 17/06/2026 — Bug fix retour user : le bouton "Accepter" s'affichait
  // pour des contrats incompatibles (T1 sur contrat frigo, chauffeur sans
  // ADR sur contrat matières dangereuses, etc.) parce qu'on ne vérifiait
  // que le tier. On délègue désormais à `canExecuteNow` qui applique les
  // VRAIES règles d'assignation (équipement véhicule, permis, spécialités
  // chauffeur) — exactement ce que la modale d'assignation exigera.
  // Note : la couverture zone-dépôt (`vehicle_wrong_depot`) est gérée à
  // l'acceptation par Rémi Forni (auto-transfert) ou un blocage explicite,
  // donc on ne bloque PAS le bouton ici sur ce critère.
  const freeDrivers = drivers.filter(d => {
    if (d.status === 'training' || d.status === 'sick' || d.status === 'vacation'
      || d.status === 'holiday' || d.status === 'striking' || d.status === 'returning') return false;
    if (d.onHoliday) return false;
    if (d.assignedVehicleId) {
      const v = vehicles.find(vv => vv.id === d.assignedVehicleId);
      if (v && v.status === 'active') return false;
    }
    return true;
  });
  const canAcceptContract = (c) =>
    canExecuteNow(c, vehicles, drivers, { activeInvestments, ownedDepots }).ready;

  // Margin calculator unlock: requires TMS investment + any commercial role.
  const hasTMS = !!(activeInvestments || {})['inv-tech-01'];
  const hasCommercial = !!(hiredSupport || {})['role-lea-bertrand']
    || !!(hiredSupport || {})['role-marc-dupuis']
    || !!(hiredSupport || {})['role-sylvie-marchand'];
  const marginUnlocked = hasTMS && hasCommercial;

  const handleAcceptContract = (contract) => {
    playSound('contractSign');
    // 11/06/2026 (soir 9) — Per-client argumentation memory : if the player
    // already picked an argument for THIS client previously, replay it
    // silently with a toast + small "Changer l'argumentation" link on the
    // card. This addresses the user feedback "rébarbatif de devoir
    // renseigner le choix à chaque contrat".
    const client = resolveClient(contract);
    const memo = client ? clientArgumentationDefaults[client.id] : null;
    if (memo && memo.choice) {
      const driversForArg = getClientDriversForContract(client, contract);
      if (driversForArg && driversForArg.primary) {
        const result = argumentationOutcome(memo.choice, driversForArg.primary, driversForArg.secondary);
        toast(`Argumentation par défaut appliquée pour ${client?.name || 'ce client'}`, {
          description: `« ${memo.choice} » — multiplicateur ×${(result.multiplier).toFixed(2)}.`,
        });
        setSelectedContract({
          ...contract,
          _priceMultiplier: result.multiplier,
          _argumentationOutcome: result.outcome,
          _argumentationFromMemory: true,
        });
        return;
      }
    }
    // 03/06/2026 Phase 3 bis — open argumentation mini-game first.
    setArgumentationContract(contract);
  };

  // Callback invoked when ContractArgumentationModal resolves.
  // priceMultiplier is 0.95 / 1 / 1.05 (or 1 when skipped).
  // 11/06/2026 — also receives the raw choice key so we can memorize it.
  const handleArgumentationConfirm = (priceMultiplier, outcome, choice) => {
    const c = argumentationContract;
    if (choice && c && rememberClientArgumentationChoice) {
      const client = resolveClient(c);
      if (client?.id) {
        rememberClientArgumentationChoice(client.id, choice);
      }
    }
    setSelectedContract({ ...c, _priceMultiplier: priceMultiplier, _argumentationOutcome: outcome });
    setArgumentationContract(null);
  };

  // 11/06/2026 — Player resets a memorized choice for a client (e.g. wants
  // to retry a different argument). Triggered by a small link on the
  // contract card.
  const handleResetArgumentationFor = (contract) => {
    const client = resolveClient(contract);
    if (!client?.id) return;
    resetClientArgumentationChoice(client.id);
    toast(`Argumentation réinitialisée pour ${client.name || 'ce client'}`, {
      description: 'La prochaine acceptation rouvrira la modale de choix.',
    });
  };

  return (
    <div className="space-y-5" data-testid="dashboard">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="overline">{t('pilot_console')} · J{totalDays}</p>
          <h1 className="font-display text-2xl md:text-3xl font-black tracking-tight text-foreground mt-0.5">
            {t('dashboard_title')}
          </h1>
        </div>
        <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-slate-400">
          <Activity className="w-3.5 h-3.5 text-accent animate-pulse-glow" />
          <span>{t('system_operational')}</span>
        </div>
      </div>

      <KpiStrip
        day={day}
        month={month}
        year={year}
        working={working}
        phase={phase}
        treasury={treasury}
        dailyDelta={dailyDelta}
        reputation={reputation}
        notoriety={notoriety}
      />

      <DayControls
        hasPending={hasPending}
        fastForwarding={fastForwarding}
        onNextDay={handleNextDay}
        onFastForward={handleFastForward}
      />

      {/* 18/09/2026 (REX apprenant) — messages urgents affichés directement. */}
      <UrgentMessagesBanner />

      <ObjectivesWidget />

      {/* 15/06/2026 — AO en cours (candidatures, contre-offres, à staffer) */}
      <TenderPipelineWidget />

      {/* 04/02/2026 — Q1 Bug Messagerie : visibilité des opportunités flash. */}
      <CommercialOpsWidget />

      {/* 05/02/2026 — Q1 Lot 2 : Salon marketing avec arc narratif (invitation + budget tiers + bilan). */}
      <MarketingSalonWidget />

      {typeof hasSupportUnlock === 'function' && hasSupportUnlock('salons') && (
        <TradeshowBanner
          month={month}
          tradeShowMonth={tradeShowMonth}
          treasury={treasury}
          onAttend={attendTradeShow}
        />
      )}

      {/* 03/06/2026 Phase 3 — view toggle (table / cork board) */}
      <div className="flex items-center justify-end gap-1.5" data-testid="contract-view-toggle">
        <button
          type="button"
          onClick={() => handleContractViewChange('table')}
          className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded transition-colors ${contractView === 'table' ? 'bg-accent text-white' : 'bg-ink-700 text-slate-300 hover:bg-ink-600'}`}
          data-testid="view-toggle-table"
          aria-pressed={contractView === 'table'}
        >
          <span className="inline-block w-3 h-3"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-full h-full"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg></span>
          Tableau
        </button>
        <button
          type="button"
          onClick={() => handleContractViewChange('cork')}
          className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded transition-colors ${contractView === 'cork' ? 'bg-accent text-white' : 'bg-ink-700 text-slate-300 hover:bg-ink-600'}`}
          data-testid="view-toggle-cork"
          aria-pressed={contractView === 'cork'}
        >
          <span className="inline-block w-3 h-3"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-full h-full"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg></span>
          Tableau de liège
        </button>
      </div>

      {contractView === 'cork' ? (
        <CorkBoardContracts
          availableContracts={availableContracts}
          drivers={drivers}
          vehicles={vehicles}
          freeDrivers={freeDrivers}
          marginUnlocked={marginUnlocked}
          canAcceptContract={canAcceptContract}
          onAccept={handleAcceptContract}
          hasMarketIntelAccess={hasMarketIntelAccess && hasMarketIntelAccess()}
          onOpenDetail={(c) => setOpenContractDetail(c)}
        />
      ) : (
        <AvailableContracts
          availableContracts={availableContracts}
          drivers={drivers}
          vehicles={vehicles}
          freeDrivers={freeDrivers}
          marginUnlocked={marginUnlocked}
          canAcceptContract={canAcceptContract}
          onAccept={handleAcceptContract}
          hasMarketIntelAccess={hasMarketIntelAccess && hasMarketIntelAccess()}
          clientArgumentationDefaults={clientArgumentationDefaults}
          onResetArgumentation={handleResetArgumentationFor}
        />
      )}

      <ContractDetailModal
        contract={openContractDetail}
        vehicles={vehicles}
        drivers={drivers}
        marginUnlocked={marginUnlocked}
        canAcceptContract={canAcceptContract}
        onAccept={(c) => { handleAcceptContract(c); setOpenContractDetail(null); }}
        onClose={() => setOpenContractDetail(null)}
      />

      <ActiveContractsList
        activeContracts={activeContracts}
        vehicles={vehicles}
        drivers={drivers}
        onCancel={setCancellingContract}
      />

      <MonthlyKpis monthlyRevenues={monthlyRevenues} monthlyExpenses={monthlyExpenses} />

      {/* 10/02/2026 (Lot J P1) — Retour user prod : « Tu peux retirer la
          vue simple du coût du carburant avec l'icône pompe si l'info est
          dans l'encart cours carburant. » FuelPriceWidget retiré, seul
          FuelEvolutionInsight (sous TreasuryChart) reste. MarketBoomWidget
          conservé puisqu'il couvre les booms sectoriels (info différente). */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="dashboard-market-row">
        <MarketBoomWidget />
        {/* Slot réservé pour futurs indicateurs marché (ex: indice
            sectoriel, taux change €/CHF…). */}
      </div>

      {/* 05/02/2026 — Q1 retour user prod : vue graphique flotte + chauffeurs
          juste sous les KPI mensuels, avec différenciation visuelle des
          statuts (disponible / en mission / indisponible / formation). */}
      <FleetOverviewWidget />

      <BenefitsCard />

      <AlertsList alerts={alerts} />

      <TreasuryChart chartData={chartData} />
      {/* 09/02/2026 — Q2 Lot I user prod : encart cours carburant + anticipation Rémi Forni. */}
      <FuelEvolutionInsight />

      <Leaderboard />

      <div className="surface-card overflow-hidden">
        <div className="px-4 py-3 border-b border-ink-600 flex items-center justify-between">
          <h3 className="font-display text-sm font-bold text-foreground tracking-tight uppercase">
            Carte stratégique
          </h3>
          <span className="text-[10px] text-slate-500 font-mono">Phase {phase}</span>
        </div>
        <div className="h-[460px] relative">
          <InteractiveMap
            hqCityId={company?.city || 'paris'}
            phase={phase}
            activeContracts={activeContracts}
            vehicles={vehicles}
            drivers={drivers}
          />
        </div>
      </div>

      {selectedContract && (
        <ContractAssignModal
          contract={selectedContract}
          onClose={() => setSelectedContract(null)}
        />
      )}

      {cancellingContract && (
        <CancelContractModal
          contract={cancellingContract}
          onClose={() => setCancellingContract(null)}
        />
      )}

      {/* 03/06/2026 Phase 3 bis — Contract argumentation mini-game */}
      {argumentationContract && (
        <ContractArgumentationModal
          contract={argumentationContract}
          onClose={() => setArgumentationContract(null)}
          onConfirm={handleArgumentationConfirm}
        />
      )}
    </div>
  );
}
